import React from "react";
import { components } from "react-select";
import ReactTooltip from "react-tooltip";
import { IconArrowDown, IconCheckThick, IconDelete, IconX } from "@rhumbix/rmbx_design_system_design_tokens";
import { customSelectStyles, OptionWrapper, StyledCheckbox, StyledClearIndicator, StyledCreatableSelect, StyledDefaultOption, StyledDropdownIndicator, StyledMultiValueRemove, StyledOption, StyledOptionTitle, StyledSelect, } from "./styles";
import Copy from "../Copy";
import DefaultActionOption from "./ActionOption";
/**
 * @prop {string} id The id attribute to assign to the selector
 * @prop {JSX.Element} ActionOption Component rendering each action option.
 * @prop {Object} actionOptionConfig ActionOption component config.
 * @prop {JSX.Element} CustomOption Component for rendering the options.
 * @prop {string[]} fixedOptions List of labels for options that should be fixed.
 * @prop {React.FunctionComponent<any> | React.ElementType} formatGroupLabel In the case where the
 *      selector presents options in groups, use this component to render each group header
 * @prop {Object|Object[]} initialSelectedOption Selected option(s).
 * @prop {HTMLDivElement} innerRef Component ref.
 * @prop {string} inputValue Search input value.
 * @prop {boolean} isClearable True if the selector should be clearable
 * @prop {boolean} isCreatable True if the selector should allow option creation
 * @prop {boolean} isDisabled True if the selector should be disabled
 * @prop {boolean} isLoading True if it is fetching options.
 * @prop {boolean} isMulti True if it is multi-select.
 * @prop {Function} onAction Callback function when an action option is selected.
 * @prop {Function} onChange Callback function when there is a change to the selected option.
 * @prop {Function} onCreateOption Callback function when a new option is created.
 * @prop {Function} onInputChange Callback function when there is a change to the search input value.
 * @prop {Function} onMenuOpen Callback function when dropdown menu opens.
 * @prop {Function} onMenuClose Callback function when dropdown menu closes.
 * @prop {Function} onMenuScrollToBottom Callback function when scrolling to the bottom of the option menu.
 * @prop {boolean} openMenuOnFocus True to open the dropdown list on focus
 * @prop {(SelectorOption | ActionOption)[]} options Options.
 * @prop {string} placeholder Placeholder text.
 * @prop {JSX.Element} SelectorOption Component rendering each option.
 * @prop {Object} selectorOptionConfig SelectorOption component config.
 * @prop {boolean} showAllWhenNoneSelected Applies styles that suggest nothing selected means all selected.
 * @prop {Partial<Styles<OptionTypeBase, boolean, GroupTypeBase<OptionTypeBase>>>} styles Additional styles to apply to the selector
 */
const Selector = ({ id, actionOptionConfig, CustomOption, fixedOptions, formatGroupLabel, initialSelectedOption, innerRef, inputValue, isClearable, isCreatable, isDisabled, isLoading, isMulti, name, onAction, onChange, onCreateOption, onInputChange, onMenuOpen, onMenuClose, onMenuScrollToBottom, openMenuOnFocus, placeholder, options, SelectorOption, selectorOptionConfig, showAllWhenNoneSelected, styles, ...rest }) => {
    const customComponents = {
        /** Format list options.
         * @param {Object} config Option configuration and data.
         * @prop {boolean} config.isSelected Whether the option is selected.
         * @return {ReactElement} Formatted list option.
         */
        Option: config => {
            const { data, isSelected } = config;
            const { isActionButton, isDisabled } = data;
            const icon = isMulti ? (React.createElement(StyledCheckbox, { checked: isSelected, disabled: isDisabled, onChange: () => null })) : (isSelected && React.createElement(IconCheckThick, { className: "icon-check" }));
            const actionHandler = (event) => {
                if (!isActionButton)
                    return;
                event.stopPropagation();
                if (onAction)
                    onAction(data);
            };
            const ActionOption = rest.ActionOption ?? DefaultActionOption;
            const Option = CustomOption ? CustomOption : isActionButton ? ActionOption : SelectorOption;
            const optionConfig = isActionButton ? actionOptionConfig : selectorOptionConfig;
            if (data.__isNew__) {
                return (React.createElement(components.Option, { ...config, className: "select-option" },
                    React.createElement(OptionWrapper, null,
                        React.createElement(StyledOption, { className: "option-content" },
                            React.createElement(StyledOptionTitle, null, data.label)))));
            }
            return (React.createElement(components.Option, { ...config, className: "select-option" },
                React.createElement(OptionWrapper, { onClick: actionHandler },
                    !isActionButton && icon,
                    // If a SelectorOption component is passed to the selector, use it.
                    Option ? (optionConfig ? (React.createElement(Option, { context: optionConfig.context, option: isActionButton ? data : data.data, customContent: data.customContent, prefixFormatter: optionConfig.prefixFormatter, primarySubtitleFormatter: optionConfig.primarySubtitleFormatter, removeSelected: false, secondarySubtitleFormatter: optionConfig.secondarySubtitleFormatter, titleFormatter: optionConfig.titleFormatter, valueFormatter: optionConfig.valueFormatter })) : (React.createElement(Option, { option: isActionButton ? data : data.data, customContent: data.customContent, removeSelected: false }))) : (React.createElement(React.Fragment, null,
                        React.createElement(StyledDefaultOption, { "data-tip": true, "data-for": `tooltip-${data.value}` }, data.label),
                        data.tooltip && (React.createElement(ReactTooltip, { id: `tooltip-${data.value}`, place: "top", type: "dark", effect: "float" },
                            React.createElement("span", null, data.tooltip))))))));
        },
        /** Clear selected options. Only displays if isClearable == true */
        ClearIndicator: config => (React.createElement(StyledClearIndicator, { ...config.innerProps, ...config }, showAllWhenNoneSelected ? React.createElement(Copy, null, "Show all") : React.createElement(IconDelete, null))),
        /** Use custom icon for the dropdown indicator */
        DropdownIndicator: config => {
            const { selectProps, isMulti } = config;
            return (React.createElement(StyledDropdownIndicator, { ...{ selectProps, isMulti } },
                React.createElement(IconArrowDown, null)));
        },
        /** IndicatorSeparator only displays when the ClearIndicator displays */
        IndicatorSeparator: config => (isClearable || isMulti) && config.hasValue ? (React.createElement(components.IndicatorSeparator, { ...config.innerProps, ...config })) : null,
        /** Format multi select - selected options */
        MultiValue: selectorOptionConfig
            ? config => {
                const { context, valueFormatter } = selectorOptionConfig;
                const value = valueFormatter
                    ? valueFormatter({ value: config.data.data, context })
                    : config.children;
                return (React.createElement(components.MultiValue, { ...config },
                    React.createElement(Copy, null, value)));
            }
            : components.MultiValue,
        MultiValueRemove: config => (React.createElement(StyledMultiValueRemove, { ...config.innerProps },
            React.createElement(IconX, null))),
    };
    const orderOptions = (values) => {
        if (!values)
            return [];
        if (!fixedOptions?.length)
            return [...values];
        const fixedValues = [];
        const nonFixedValues = [];
        // if an option's label is set to be fixed, add isFixed value
        values.forEach(v => {
            fixedOptions.includes(v.value) ? fixedValues.push({ ...v, isFixed: true }) : nonFixedValues.push(v);
        });
        return fixedValues.concat(nonFixedValues);
    };
    const handleChange = (value, actionMeta) => {
        // If there are no fixed options, just send the value to onChange
        if (!fixedOptions?.length) {
            if (onChange)
                onChange(value);
            return;
        }
        let newValue = Array.isArray(value) ? value : [value];
        const { action } = actionMeta;
        // if we have fixed options we want to block them from being removed.
        // if the selector is cleared, fixed options will still remain
        switch (action) {
            case "remove-value":
            case "pop-value":
                if (fixedOptions?.includes(actionMeta.removedValue.value)) {
                    return;
                }
                break;
            case "clear":
                newValue = options?.filter(value => fixedOptions?.includes(value.value)) ?? [];
                break;
        }
        // we want to put fixed options at the front so they are always together
        newValue = orderOptions(newValue);
        if (onChange)
            onChange(newValue);
    };
    return isCreatable ? (React.createElement(StyledCreatableSelect, { id: id, closeMenuOnSelect: !isMulti, components: customComponents, filterOption: (option) => !!option, formatGroupLabel: formatGroupLabel, hideSelectedOptions: false, inputValue: inputValue, placeholder: placeholder, isClearable: isClearable, isDisabled: isDisabled, isLoading: isLoading, isMulti: isMulti, name: name, onChange: handleChange, onCreateOption: onCreateOption, onInputChange: onInputChange, onMenuClose: onMenuClose, onMenuOpen: onMenuOpen, onMenuScrollToBottom: onMenuScrollToBottom, openMenuOnFocus: openMenuOnFocus, options: orderOptions(options), ref: innerRef, showAllWhenNoneSelected: showAllWhenNoneSelected, styles: { ...customSelectStyles, ...(styles ?? {}) }, value: Array.isArray(initialSelectedOption) ? orderOptions(initialSelectedOption) : initialSelectedOption })) : (React.createElement(StyledSelect, { id: id, closeMenuOnSelect: !isMulti, components: customComponents, filterOption: (option) => !!option, formatGroupLabel: formatGroupLabel, hideSelectedOptions: false, inputValue: inputValue, placeholder: placeholder, isClearable: isClearable, isDisabled: isDisabled, isLoading: isLoading, isMulti: isMulti, name: name, onChange: handleChange, onInputChange: onInputChange, onMenuClose: onMenuClose, onMenuOpen: onMenuOpen, onMenuScrollToBottom: onMenuScrollToBottom, openMenuOnFocus: openMenuOnFocus, options: orderOptions(options), ref: innerRef, showAllWhenNoneSelected: showAllWhenNoneSelected, styles: { ...customSelectStyles, ...(styles ?? {}) }, value: Array.isArray(initialSelectedOption) ? orderOptions(initialSelectedOption) : initialSelectedOption }));
};
export default Selector;
